import { useEffect, useState } from 'react';
import { CheckCircle, XCircle } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { SkeletonRows } from '../../components/skeleton';
import { Pagination } from '../../components/pagination';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { extractErr } from '../../api/error-message';
import { useConfirm } from '../../components/confirm-dialog';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { useUrlTab } from '../../services/attention';

const PER_PAGE = 15;

// Wired to GET /admin/payouts + approve/reject. Sellers and delivery partners
// request withdrawals from their wallet; admins settle them here.
//
// Paged and searched by the API (page/limit/q). The page used to fetch with no
// paging params and slice locally, so it only ever saw the API's default first
// 25 rows of a tab.

interface Payout {
  id: string;
  beneficiaryType: 'SELLER' | 'DELIVERY_PARTNER';
  beneficiaryId: string;
  bankAccountId: string;
  amount: string;
  status: string;
  failureReason: string | null;
  paidAt: string | null;
  createdAt: string;
  // Rail bookkeeping. RAZORPAYX means the transfer was handed to the payout
  // rail and only its webhook may mark the row paid; MANUAL means an admin
  // moved the money by hand and typed the reference in.
  payoutRail: string | null;
  gatewayPayoutId: string | null;
  utr: string | null;
}

const STATUS_TABS = ['QUEUED', 'INITIATED', 'PAID', 'FAILED', 'ALL'] as const;
type StatusKey = (typeof STATUS_TABS)[number];

function errCode(err: unknown): string | null {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? null;
}

const STATUS_STYLE: Record<string, string> = {
  QUEUED: 'badge-warning',
  INITIATED: 'badge-info',
  PAID: 'badge-success',
  FAILED: 'badge-danger',
};

// Display label for a payout status (tabs and badges) — sentence case.
function statusLabel(s: string): string {
  if (s === 'QUEUED') return 'Pending';
  if (s === 'INITIATED') return 'Sent to bank';
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function beneficiaryLabel(p: Payout): string {
  return `${p.beneficiaryType === 'SELLER' ? 'Seller' : 'Delivery partner'} #${p.beneficiaryId}`;
}

function amountLabel(p: Payout): string {
  return `₹${Number(p.amount).toLocaleString('en-IN')}`;
}

export function PayoutsPage() {
  const { prompt } = useConfirm();
  // In the URL (?tab=) so the dashboard / sidebar can open a specific queue.
  const [status, setStatus] = useUrlTab<StatusKey>(STATUS_TABS, 'QUEUED');
  const [rows, setRows] = useState<Payout[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Skeleton on the first fetch only; later fetches keep the table (and the
  // search box's focus) in place.
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Server-side search over every column the table shows (ref, beneficiary,
  // bank account, amount, status, failure reason, UTR, rail, requested date).
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 300);
  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  // A new tab or query is a new result set — back to page 1.
  useEffect(() => { setPage(1); }, [status, q]);

  // Guard against out-of-order responses when the tab, query or page changes quickly.
  useEffect(() => {
    let alive = true;
    api
      .get<{ data: Payout[]; pagination?: { total: number } }>('/admin/payouts', {
        params: { status, page, limit: PER_PAGE, ...(q ? { q } : {}) },
      })
      .then(({ data }) => {
        if (!alive) return;
        setRows(data.data);
        setTotal(data.pagination?.total ?? data.data.length);
      })
      .catch((err) => { if (alive) toast.error(extractErr(err)); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [status, q, page, reloadKey]);

  const act = async (p: Payout, action: 'approve' | 'reject') => {
    let body: Record<string, unknown> | undefined;
    if (action === 'reject') {
      const reason = await prompt({
        title: `Reject withdrawal #${p.id}?`,
        description: `${amountLabel(p)} goes back to the ${beneficiaryLabel(p).toLowerCase()}'s wallet and nothing is sent to the bank. They can request a new withdrawal.`,
        label: 'Reason for rejecting',
        placeholder: 'e.g. Bank account details could not be verified',
        required: true,
        multiline: true,
        confirmLabel: 'Reject withdrawal',
        tone: 'danger',
      });
      if (reason === null) return;
      body = { reason };
    }
    setBusyId(p.id);
    try {
      await api.post(`/admin/payouts/${p.id}/${action}`, body);
      toast.success(
        action === 'approve'
          ? 'Withdrawal sent to the bank'
          : 'Withdrawal rejected & refunded to wallet',
      );
      load();
    } catch (err) {
      // With the payout rail switched off the transfer is made by hand, so the
      // API asks for the bank's UTR before it will record the row as paid.
      // Prompting on demand keeps the common (rail on) path a single click
      // rather than asking every admin for a reference they may not have.
      if (action === 'approve' && errCode(err) === 'UTR_REQUIRED') {
        const utr = await prompt({
          title: `Record withdrawal #${p.id} as paid?`,
          description: `The payout rail is off, so this transfer is made by hand. Enter the bank UTR for the ${amountLabel(p)} you sent to ${beneficiaryLabel(p)}; the payout is then marked Paid.`,
          label: 'Bank UTR',
          placeholder: 'e.g. 412345678901',
          required: true,
          confirmLabel: 'Mark as paid',
          tone: 'primary',
        });
        if (utr) {
          try {
            await api.post(`/admin/payouts/${p.id}/approve`, { utr });
            toast.success('Payout recorded as paid');
            load();
          } catch (retryErr) {
            toast.error(extractErr(retryErr));
          }
        }
        return;
      }
      toast.error(extractErr(err));
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="space-y-6">
      <PageHeader title="Payouts" description="Seller &amp; delivery-partner wallet withdrawals" />

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setStatus(t)}
            aria-pressed={status === t}
            className="fchip"
          >
            {statusLabel(t)}
          </button>
        ))}
      </div>

      <TableSearch
        value={search}
        onChange={setSearch}
        placeholder="Search by ref, seller or partner, amount, status, UTR or date"
        resultCount={q ? total : undefined}
      />

      <div className="panel overflow-hidden">
        {!loaded ? (
          <SkeletonRows cols={5} />
        ) : rows.length === 0 && q ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for “${q}”`}
            body="Try a payout ref, beneficiary id, amount or UTR."
            action={
              <button type="button" onClick={() => setSearch('')} className="btn btn-secondary btn-sm">
                Clear search
              </button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="account_balance_wallet"
            title="No payouts in this view"
            body="Withdrawal requests from sellers and delivery partners appear here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Beneficiary</th>
                  <th>Bank account</th>
                  <th className="!text-right">Amount</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th className="!text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="sku text-ink-500">#{p.id}</td>
                    <td className="text-ink-700">
                      {p.beneficiaryType === 'SELLER' ? 'Seller' : 'Delivery partner'} <span className="sku">#{p.beneficiaryId}</span>
                    </td>
                    <td className="sku text-ink-500">#{p.bankAccountId}</td>
                    <td className="text-right font-tabular font-semibold text-ink-900">₹{Number(p.amount).toLocaleString('en-IN')}</td>
                    <td>
                      <span className={`badge ${STATUS_STYLE[p.status] ?? ''}`}>
                        {statusLabel(p.status)}
                      </span>
                      {p.failureReason && <p className="mt-1 text-[12.5px] text-danger-ink">{p.failureReason}</p>}
                      {/* The UTR is what a seller quotes to their own bank, so it is
                          the one field that makes a PAID row independently checkable. */}
                      {p.utr && <p className="sku mt-1 text-ink-500">UTR {p.utr}</p>}
                      {p.payoutRail && (
                        <p className="mt-1 text-[12.5px] text-ink-500">
                          {p.payoutRail === 'RAZORPAYX' ? 'Via RazorpayX' : 'Paid manually'}
                        </p>
                      )}
                    </td>
                    <td className="text-ink-500">{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                    <td className="text-right">
                      {/* Once the rail holds the payout, nobody here decides its
                          outcome — the bank does, and the webhook records it.
                          Offering Reject would credit the wallet back while the
                          transfer is still in flight. */}
                      {p.gatewayPayoutId ? (
                        <span className="text-[12.5px] text-ink-500">Awaiting bank</span>
                      ) : p.status === 'QUEUED' || p.status === 'INITIATED' ? (
                        <div className="flex justify-end gap-2">
                          <button type="button" disabled={busyId === p.id} onClick={() => void act(p, 'approve')} className="btn btn-success btn-sm"><CheckCircle size={16} /> Approve</button>
                          <button type="button" disabled={busyId === p.id} onClick={() => void act(p, 'reject')} className="btn btn-danger-outline btn-sm"><XCircle size={16} /> Reject</button>
                        </div>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {loaded && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={total}
            perPage={PER_PAGE}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
